import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { EVA_RESEARCH_NAME } from "constants/app.constants";
import { MCDANIEL_SEARCH_FOLDER_NAME } from "constants/settings.constants";
import { RootState } from "store/rootReducer";
import formatPropertyName from "utils/column/formatPropertyName";
import getColumnLabelWithContext from "utils/column/getColumnLabelWithContext";
import getGroupByListColumnByProperty from "utils/column/getGroupByListColumnByProperty";

import { IGroupByListColumn } from "models";

import { isForecastQualifiedProperty } from "components/groupBy/helpers/qualifyForecastProperty";

import { EntityKind } from "../models/entityKind";

export interface IProperty extends IGroupByListColumn {
  fullContextTitle: string;
  fullContextTitleWithNormalization: string;
  lightContextTitle: string;
  partialContextTitle: string;
}

export default function useProperty(
  propertyName: string,
  forecastToggleOn = false,
  selectedForecastFolder?: string,
  entityKind: EntityKind = EntityKind.Well,
  performanceDateRange?: { startDate: string; endDate: string }
): IProperty {
  const [property, setProperty] = useState<IProperty>(null);
  // Get category properties for column and header
  const categories = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.categoryList
      : state.groupBy.facilityFields
  );

  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );

  const globalUseNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );

  // Read here because the effect below strips the forecast suffix off propertyName
  // in place, and the suffix is the only record of whether a forecast was applied.
  const carriesForecast = isForecastQualifiedProperty(propertyName);

  useEffect(() => {
    if (!categories) return;

    propertyName = formatPropertyName(propertyName);

    const newProp = getGroupByListColumnByProperty(categories, propertyName);
    if (newProp) {
      // A Performance field is labelled with the forecast source only when its property
      // says a forecast was used. Left alone under EVA Research, which stores its
      // forecast fields with a bare property on purpose.
      const labelForecastToggleOn =
        forecastToggleOn &&
        newProp.group === "Performance" &&
        selectedForecastFolder !== EVA_RESEARCH_NAME &&
        selectedForecastFolder !== MCDANIEL_SEARCH_FOLDER_NAME
          ? carriesForecast
          : forecastToggleOn;

      const contextTitle = getColumnLabelWithContext(
        newProp,
        globalUseNormalizeBy,
        globalNormalizeBy,
        labelForecastToggleOn,
        selectedForecastFolder,
        entityKind,
        performanceDateRange
      );
      const fullContextTitle = contextTitle.fullContextTitle;
      const fullContextTitleWithNormalization =
        contextTitle.fullContextTitleWithNormalization;
      const lightContextTitle = contextTitle.lightContextTitle;
      const partialContextTitle = contextTitle.partialContextTitle;

      setProperty({
        ...newProp,
        fullContextTitle,
        fullContextTitleWithNormalization,
        lightContextTitle,
        partialContextTitle
      });
    }
    // The forecast inputs are listed here even though most fields fold them into
    // propertyName: the source a field is stored under can move without the property
    // moving with it, and the title then kept the old source name.
  }, [
    categories,
    globalNormalizeBy,
    globalUseNormalizeBy,
    propertyName,
    performanceDateRange,
    forecastToggleOn,
    selectedForecastFolder
  ]);

  useEffect(() => {
    if (!property) return;
  }, [property]);

  return property;
}
